import { useCallback, useMemo } from "react"
import { DESC } from "helpers/sorts"
import { useFactorViewDashboardMetrics } from "helpers/metrics/hooks"
import { useTemplateGatherers } from "connected-components/dashboard/hooks/use-template-gatherers"
import { CONTENT_SET_DATA_TYPES } from "helpers/contentSetConstants"
import { useAnalytics } from "connected-components/analytics"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { useDrilldownOpener } from "connected-components/dashboard/drilldown/use-drilldown-opener"
import { buildDrilldownIntent } from "connected-components/dashboard/drilldown/build-drilldown-intent"
import { resolveIntradayDrilldownFields } from "connected-components/dashboard/drilldown/resolve-intraday-drilldown-fields"
import { useFactorsViewTableHandlers } from "../../hooks/use-factors-view-table-handlers"
import ContributorsDataIntegrator from "gatherers/contributors-data-integrator"
import ContributorsTable from "renderers/contributors-datagrid"
import { INSTRUMENTS } from "connected-components/dashboard/helpers"
import { useFactorsViewData } from "connected-components/factors-view/hooks/use-factors-view-data"
import { useDashboardSliceData } from "connected-components/dashboard/hooks/use-dashboard-slice-data"
import { FACTORS_VIEW_PREFERENCE_BASE } from "connected-components/dashboard/store/factors-view-slice"
import { getToday } from "helpers/types/dates"
import { useFactorsViewTemplateChart } from "../../hooks/use-factors-view-template-chart"
import { FACTORS_VIEW_CONTENT_SET_TEMPLATE_KEY, FACTORS_VIEW_CONTENT_SET_DEFAULTS } from "../template-chart-config"
import { FACTORS_VIEW_COMMON_PROPS } from "../../prop-types"

export default function FactorsContentSetView({ instrumentsWithMetadata, instrumentColumns, selectedContentSetMetadata }) {
  const contextData = useFactorsViewData()
  const { sortMetricId, setSortMetricId, sortDirection, setSortDirection, selectedTableLevels, setSelectedTableLevels, view: contentSetId, isEndVsStartDateComparison, instrument, comparisonInstrument, secondInstrument, base, filter, normalization, modelId, intradayBatchMetadata, intradayPricingAsOf } = contextData

  // No header control here to view/clear a panel dateOverride, so this reads the dashboard's own
  // dates/intraday state directly instead of useFactorsViewData's panel-effective ones, and feeds
  // that into every date-sensitive read below (fetch, intraday branch, and drilldown) consistently.
  const { core } = useDashboardSliceData(FACTORS_VIEW_PREFERENCE_BASE)
  const startDate = core.startDate
  const endDate = core.endDate
  const isIntradayMode = core.isIntraday && endDate === getToday()
  const dashboardScopedContextData = useMemo(
    () => ({ ...contextData, startDate, endDate, isIntradayMode }),
    [contextData, startDate, endDate, isIntradayMode],
  )

  const { selectedCategory } = useFactorsViewTemplateChart(
    FACTORS_VIEW_CONTENT_SET_TEMPLATE_KEY,
    FACTORS_VIEW_CONTENT_SET_DEFAULTS,
  )

  const analytics = useAnalytics()
  const { handleSortChanged, handleTableNavigationChanged } = useFactorsViewTableHandlers({ track: analytics.track })
  const panelId = usePanelId()
  const openDrilldown = useDrilldownOpener()

  const handleDrilldownRowClick = useCallback((factorId, clickPosition) => {
    if (!panelId || !contentSetId) return
    openDrilldown(buildDrilldownIntent({
      panelId,
      sourceSurface: "factors.content-set",
      title: "Asset View: Content Set",
      view: contentSetId,
      context: {
        modelId,
        instrument,
        comparisonInstrument,
        secondInstrument,
        base,
        startDate,
        endDate,
        isIntradayMode,
        ...resolveIntradayDrilldownFields(isIntradayMode, intradayBatchMetadata, intradayPricingAsOf),
        filter,
        normalization,
        isEndVsStartDateComparison,
      },
      clickPosition,
      seed: {
        selectedFactor: factorId,
        selectedCategory,
      },
    }))
  }, [panelId, openDrilldown, contentSetId, selectedCategory, modelId, instrument, comparisonInstrument, secondInstrument, base, startDate, endDate, isIntradayMode, intradayBatchMetadata, intradayPricingAsOf, filter, normalization, isEndVsStartDateComparison])

  const gatherers = useTemplateGatherers(dashboardScopedContextData, "factorContentSet", { contentSetId, useCompositionQueries: false, selectedCategory })

  const metrics = useFactorViewDashboardMetrics(
    [{ category: "factorContentSet", isSensitivity: selectedContentSetMetadata.dataType === CONTENT_SET_DATA_TYPES.SENSITIVITY }],
    { contentSetId, instrumentColumns, instrumentsWithMetadata, isEndVsStartDateComparison },
  )

  const defaultSortSuffix = INSTRUMENTS[instrumentColumns[0]].suffix

  return <ContributorsDataIntegrator
    modelId={modelId}
    mainInstrument={instrument}
    gatherers={gatherers}
    metrics={metrics}
    Renderer={ContributorsTable}
    rendererProps={{
      groupByName: "Category",
      defaultSort: {
        path: `contentSetNetContribution${defaultSortSuffix}`,
        direction: DESC,
      },
      disableCategories: true,
      classes: "factors-table",
      idLabel: "Factor",
      hidePagination: true,
      includeDrilldownArrows: true,
      downloadKey: "factors.contentSet",
      downloadName: "Factors - Content Set",
      sortMetricId,
      onSortMetricIdChange: setSortMetricId,
      sortDirection,
      onSortDirectionChange: setSortDirection,
      tableNavigationLevels: selectedTableLevels,
      onTableNavigationChange: setSelectedTableLevels,
      onSortChanged: handleSortChanged,
      onTableNavigationChanged: handleTableNavigationChanged,
      onDrilldownRowClick: handleDrilldownRowClick,
      viewLabel: selectedContentSetMetadata?.name,
    }}
  />
}

FactorsContentSetView.propTypes = FACTORS_VIEW_COMMON_PROPS
